// src/pages/finance-portal/dashboard/FinanceDashboard.js
// Comprehensive Finance Dashboard combining Module 1 (Banking) + Module 2 (GL/COA)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Grid, Card, CardContent, Paper, Button,
    Chip, CircularProgress, Skeleton, Alert, LinearProgress, Divider,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SavingsIcon from '@mui/icons-material/Savings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import LinkIcon from '@mui/icons-material/Link';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AccountSetupWizard from './AccountSetupWizard';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const ACCOUNT_TYPE_ICONS = {
    DEPOSITORY: <SavingsIcon sx={{ fontSize: 20 }} />,
    CREDIT: <CreditCardIcon sx={{ fontSize: 20 }} />,
    LOAN: <ReceiptLongIcon sx={{ fontSize: 20 }} />,
    INVESTMENT: <TrendingUpIcon sx={{ fontSize: 20 }} />,
    OTHER: <AccountBalanceWalletIcon sx={{ fontSize: 20 }} />,
};

const ACCOUNT_TYPE_COLORS = {
    DEPOSITORY: '#22c55e', CREDIT: '#f59e0b', LOAN: '#ef4444', INVESTMENT: '#8b5cf6', OTHER: '#64748b',
};

const STATUS_COLORS = {
    DRAFT: { bg: '#fef3c7', color: '#92400e' },
    POSTED: { bg: '#dcfce7', color: '#166534' },
    VOIDED: { bg: '#fecaca', color: '#991b1b' },
};

function formatCurrency(amount) {
    if (amount == null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

// Stat card component
function StatCard({ icon, label, value, subtitle, color = ACCENT, onClick }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                '&:hover': onClick ? { borderColor: color, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' } : {},
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ width: 42, height: 42, borderRadius: '12px', bgcolor: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
                        {icon}
                    </Box>
                    {onClick && <ArrowForwardIcon sx={{ fontSize: 16, color: '#cbd5e1' }} />}
                </Box>
                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {label}
                </Typography>
                <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', color: '#1e293b', mt: 0.25, lineHeight: 1.2 }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8', mt: 0.5 }}>
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

export default function FinanceDashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [plaidSetup, setPlaidSetup] = useState(null);
    const [coaSetup, setCoaSetup] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [summaryRes, plaidRes, coaRes] = await Promise.all([
                axiosInstance.get('/api/finance/dashboard-summary').catch(() => null),
                axiosInstance.get('/api/finance/plaid/setup-status').catch(() => null),
                axiosInstance.get('/api/finance/coa/setup-status').catch(() => null),
            ]);

            if (summaryRes?.data?.success) setData(summaryRes.data);
            if (plaidRes?.data?.success) setPlaidSetup(plaidRes.data);
            if (coaRes?.data?.success) setCoaSetup(coaRes.data);
        } catch (err) {
            setError('Failed to load dashboard data');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const hasBank = plaidSetup?.has_accounts;
    const hasCOA = coaSetup?.is_setup;
    const isFullySetup = hasBank && hasCOA;

    // Loading state
    if (isLoading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
                <Paper elevation={0} sx={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', borderRadius: { xs: 0, md: '0 0 24px 24px' }, px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3 }}>
                    <Skeleton variant="text" width={250} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                </Paper>
                <Box sx={{ px: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
                    <Grid container spacing={2.5}>
                        {[1, 2, 3, 4].map(i => <Grid item xs={12} sm={6} md={3} key={i}><Skeleton variant="rounded" height={140} sx={{ borderRadius: '16px' }} /></Grid>)}
                    </Grid>
                </Box>
            </Box>
        );
    }

    const banking = data?.banking || {};
    const ledger = data?.ledger || {};
    const recentEntries = data?.recent_entries || [];
    const netIncome = ledger.net_income || 0;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AccountBalanceIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                Finance Dashboard
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                {isFullySetup ? 'Your financial overview at a glance' : 'Complete setup to unlock your dashboard'}
                            </Typography>
                        </Box>
                    </Box>

                    {isFullySetup && (
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3 }}>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>Cash Balance</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 800, color: '#fff', fontSize: '1.4rem' }}>{formatCurrency(banking.total_cash)}</Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.15)' }} />
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>Net Income (MTD)</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 800, color: netIncome >= 0 ? '#fff' : '#fecaca', fontSize: '1.4rem' }}>{formatCurrency(netIncome)}</Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Setup Checklist (if not fully set up) */}
                {!isFullySetup && (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: '#1e293b', mb: 0.5 }}>
                                Getting Started
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 2.5 }}>
                                Complete these steps to set up your finance portal
                            </Typography>

                            {/* Step 1: Connect Bank */}
                            <Box
                                onClick={() => !hasBank && navigate('/finance-portal/connected-accounts')}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: '12px',
                                    border: `1px solid ${hasBank ? '#dcfce7' : '#e2e8f0'}`,
                                    bgcolor: hasBank ? '#f0fdf4' : '#fff',
                                    cursor: hasBank ? 'default' : 'pointer',
                                    mb: 1.5,
                                    '&:hover': !hasBank ? { borderColor: ACCENT } : {},
                                }}
                            >
                                <Box sx={{
                                    width: 40, height: 40, borderRadius: '10px',
                                    bgcolor: hasBank ? '#dcfce7' : 'rgba(34,197,94,0.1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {hasBank ? <CheckCircleIcon sx={{ color: ACCENT }} /> : <LinkIcon sx={{ color: ACCENT }} />}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                                        Connect Bank Accounts
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                        {hasBank ? `${banking.connected_banks} bank(s), ${banking.total_accounts} account(s) connected` : 'Link your bank via Plaid to import transactions'}
                                    </Typography>
                                </Box>
                                {hasBank ? (
                                    <Chip label="Complete" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#dcfce7', color: '#166534' }} />
                                ) : (
                                    <ArrowForwardIcon sx={{ color: '#94a3b8' }} />
                                )}
                            </Box>

                            {/* Step 2: Chart of Accounts */}
                            <Box
                                onClick={() => !hasCOA && navigate('/finance-portal/chart-of-accounts')}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: '12px',
                                    border: `1px solid ${hasCOA ? '#dcfce7' : '#e2e8f0'}`,
                                    bgcolor: hasCOA ? '#f0fdf4' : '#fff',
                                    cursor: hasCOA ? 'default' : 'pointer',
                                    '&:hover': !hasCOA ? { borderColor: ACCENT } : {},
                                }}
                            >
                                <Box sx={{
                                    width: 40, height: 40, borderRadius: '10px',
                                    bgcolor: hasCOA ? '#dcfce7' : 'rgba(34,197,94,0.1)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {hasCOA ? <CheckCircleIcon sx={{ color: ACCENT }} /> : <AccountTreeIcon sx={{ color: ACCENT }} />}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                                        Set Up Chart of Accounts
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                        {hasCOA ? `${coaSetup.account_count} accounts configured` : 'Initialize your accounting structure'}
                                    </Typography>
                                </Box>
                                {hasCOA ? (
                                    <Chip label="Complete" size="small" sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: '#dcfce7', color: '#166534' }} />
                                ) : (
                                    <ArrowForwardIcon sx={{ color: '#94a3b8' }} />
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Main Dashboard Content (visible when at least partially set up) */}
                {(hasBank || hasCOA) && (
                    <>
                        {/* KPI Row */}
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    icon={<SavingsIcon />}
                                    label="Cash Balance"
                                    value={formatCurrency(banking.total_cash)}
                                    subtitle={`${banking.total_accounts || 0} bank accounts`}
                                    color="#22c55e"
                                    onClick={() => navigate('/finance-portal/connected-accounts')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    icon={<CreditCardIcon />}
                                    label="Credit Used"
                                    value={formatCurrency(banking.total_credit_used)}
                                    subtitle={banking.total_credit_limit ? `of ${formatCurrency(banking.total_credit_limit)} limit` : 'No credit accounts'}
                                    color="#f59e0b"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    icon={<TrendingUpIcon />}
                                    label="Revenue (MTD)"
                                    value={formatCurrency(ledger.monthly_revenue)}
                                    subtitle="This month"
                                    color="#3b82f6"
                                    onClick={() => navigate('/finance-portal/trial-balance')}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    icon={<TrendingDownIcon />}
                                    label="Expenses (MTD)"
                                    value={formatCurrency(ledger.monthly_expenses)}
                                    subtitle={`Net: ${formatCurrency(netIncome)}`}
                                    color="#ef4444"
                                    onClick={() => navigate('/finance-portal/trial-balance')}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2.5}>
                            {/* Left Column: Bank Accounts */}
                            <Grid item xs={12} md={7}>
                                {/* Connected Bank Accounts */}
                                {hasBank && banking.accounts?.length > 0 && (
                                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2.5 }}>
                                        <CardContent sx={{ p: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                                    Bank Accounts
                                                </Typography>
                                                <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => navigate('/finance-portal/connected-accounts')}
                                                    sx={{ fontFamily, textTransform: 'none', fontSize: '0.8rem', color: ACCENT }}
                                                >
                                                    View All
                                                </Button>
                                            </Box>
                                            {banking.accounts.map((account, idx) => {
                                                const typeColor = ACCOUNT_TYPE_COLORS[account.type] || '#64748b';
                                                return (
                                                    <Box key={account.id} sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                                        px: 2.5, py: 1.5,
                                                        borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none',
                                                    }}>
                                                        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: `${typeColor}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: typeColor }}>
                                                            {ACCOUNT_TYPE_ICONS[account.type] || ACCOUNT_TYPE_ICONS.OTHER}
                                                        </Box>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {account.name}
                                                            </Typography>
                                                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>
                                                                {account.institution_name} {account.mask ? `••${account.mask}` : ''}
                                                            </Typography>
                                                        </Box>
                                                        <Typography sx={{
                                                            fontFamily, fontWeight: 700, fontSize: '0.95rem',
                                                            color: (account.current_balance || 0) >= 0 ? '#1e293b' : '#ef4444',
                                                        }}>
                                                            {formatCurrency(account.current_balance)}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </CardContent>
                                    </Card>
                                )}

                                {/* GL Balance by Type */}
                                {hasCOA && ledger.balance_by_type && Object.keys(ledger.balance_by_type).length > 0 && (
                                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                        <CardContent sx={{ p: 2.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                                    Account Balances by Type
                                                </Typography>
                                                <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => navigate('/finance-portal/trial-balance')}
                                                    sx={{ fontFamily, textTransform: 'none', fontSize: '0.8rem', color: ACCENT }}
                                                >
                                                    Trial Balance
                                                </Button>
                                            </Box>
                                            {['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'].map(type => {
                                                const bal = ledger.balance_by_type[type];
                                                if (bal == null) return null;
                                                const colors = { ASSET: '#22c55e', LIABILITY: '#ef4444', EQUITY: '#8b5cf6', REVENUE: '#3b82f6', EXPENSE: '#f59e0b' };
                                                return (
                                                    <Box key={type} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f8fafc' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colors[type] }} />
                                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500, color: '#475569' }}>{type}</Typography>
                                                        </Box>
                                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>
                                                            {formatCurrency(bal)}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </CardContent>
                                    </Card>
                                )}
                            </Grid>

                            {/* Right Column: Activity & Quick Actions */}
                            <Grid item xs={12} md={5}>
                                {/* Quick Actions */}
                                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2.5 }}>
                                    <CardContent sx={{ p: 2.5 }}>
                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>
                                            Quick Actions
                                        </Typography>
                                        <Grid container spacing={1.5}>
                                            {[
                                                { label: 'Journal Entry', icon: <MenuBookIcon />, path: '/finance-portal/journal-entries', color: '#3b82f6' },
                                                { label: 'Chart of Accounts', icon: <AccountTreeIcon />, path: '/finance-portal/chart-of-accounts', color: '#22c55e' },
                                                { label: 'Trial Balance', icon: <AssessmentIcon />, path: '/finance-portal/trial-balance', color: '#8b5cf6' },
                                                { label: 'Bank Accounts', icon: <AccountBalanceIcon />, path: '/finance-portal/connected-accounts', color: '#f59e0b' },
                                            ].map(action => (
                                                <Grid item xs={6} key={action.label}>
                                                    <Box
                                                        onClick={() => navigate(action.path)}
                                                        sx={{
                                                            p: 2, borderRadius: '12px', border: '1px solid #e2e8f0',
                                                            cursor: 'pointer', textAlign: 'center',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': { borderColor: action.color, bgcolor: `${action.color}06`, transform: 'translateY(-1px)' },
                                                        }}
                                                    >
                                                        <Box sx={{ color: action.color, mb: 0.5 }}>{action.icon}</Box>
                                                        <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>{action.label}</Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </CardContent>
                                </Card>

                                {/* Ledger Stats */}
                                {hasCOA && (
                                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2.5 }}>
                                        <CardContent sx={{ p: 2.5 }}>
                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>
                                                Ledger Overview
                                            </Typography>
                                            {[
                                                { label: 'Chart of Accounts', value: ledger.coa_count || 0 },
                                                { label: 'Total Journal Entries', value: ledger.total_entries || 0 },
                                                { label: 'Posted Entries', value: ledger.posted_entries || 0, color: '#166534' },
                                                { label: 'Draft Entries', value: ledger.draft_entries || 0, color: '#92400e' },
                                            ].map((item, idx) => (
                                                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: idx < 3 ? '1px solid #f8fafc' : 'none' }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>{item.label}</Typography>
                                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: item.color || '#1e293b' }}>{item.value}</Typography>
                                                </Box>
                                            ))}
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Recent Journal Entries */}
                                {recentEntries.length > 0 && (
                                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                                        <CardContent sx={{ p: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                                    Recent Entries
                                                </Typography>
                                                <Button size="small" endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => navigate('/finance-portal/journal-entries')}
                                                    sx={{ fontFamily, textTransform: 'none', fontSize: '0.8rem', color: ACCENT }}
                                                >
                                                    View All
                                                </Button>
                                            </Box>
                                            {recentEntries.map((entry, idx) => {
                                                const st = STATUS_COLORS[entry.status] || {};
                                                return (
                                                    <Box key={entry.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 1.5, borderTop: idx > 0 ? '1px solid #f1f5f9' : 'none' }}>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.82rem', color: '#1e293b' }}>{entry.entry_number}</Typography>
                                                                <Chip label={entry.status} size="small" sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 600, height: 18, bgcolor: st.bg, color: st.color }} />
                                                            </Box>
                                                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {entry.description || entry.entry_date}
                                                            </Typography>
                                                        </Box>
                                                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.82rem', color: '#1e293b' }}>
                                                            {formatCurrency(entry.total_debit)}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </CardContent>
                                    </Card>
                                )}
                            </Grid>
                        </Grid>
                    </>
                )}

                {/* Show setup wizard only if nothing is set up at all */}
                {!hasBank && !hasCOA && !isLoading && (
                    <Box sx={{ mt: 2 }}>
                        <AccountSetupWizard onSetupComplete={loadData} />
                    </Box>
                )}
            </Box>
        </Box>
    );
}
